import type React from 'react';
import type { ReactNode, HTMLAttributes } from 'react';

export const AI_ASSISTANT_CONVERSATIONS_KEY = 'ai-assistant-conversations';
export const AI_ASSISTANT_COHORTS_KEY = 'ai-assistant-cohorts';
export const INPUT_MIN_HEIGHT = 46;
export const INPUT_MAX_HEIGHT = 200;

interface MarkdownTableProps extends HTMLAttributes<HTMLTableElement> {
  children?: ReactNode;
  className?: string;
}

export const AI_ASSISTANT_TABLE_CLASSES = {
  wrapper: 'overflow-x-auto my-3',
  table: 'w-full min-w-0 border border-gray-200 border-collapse',
  th: 'border border-gray-200 px-3 py-2 text-left font-semibold bg-gray-50',
  td: 'border border-gray-200 px-3 py-2 text-left font-normal',
} as const;

export const MARKDOWN_OVERRIDES = {
  ul: {
    props: {
      className: 'list-disc list-inside pl-4 my-2 space-y-1',
    },
  },
  ol: {
    props: {
      className: 'list-decimal list-inside pl-4 my-2 space-y-1',
    },
  },
  a: {
    props: {
      className: 'underline text-blue-700',
    },
  },
  hr: {
    props: {
      className: 'my-3 border-gray-200',
    },
  },
  table: {
    component: ({
      children,
      className,
      ...rest
    }: MarkdownTableProps): React.JSX.Element => (
      <div className={AI_ASSISTANT_TABLE_CLASSES.wrapper}>
        <table className={className} {...rest}>
          {children}
        </table>
      </div>
    ),
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.table,
    },
  },
  th: {
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.th,
    },
  },
  td: {
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.td,
    },
  },
};

export const FEEDBACK_TYPES = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
};

export const TERMS_URL = 'https://marketviewedu.com/terms-conditions/';
export const ONBOARDING_KEY_PREFIX = 'ai-assistant-onboarding-dismissed';
export const SIDEBAR_ITEM_SELECTOR =
  '[data-testid="side-menu-item-ai-assistant"]';
export const GUIDE_FAQ_URL =
  'https://app.marketviewedu.com/learn/content/guides/18';
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const POPUP_GAP = 16;
export const SIDEBAR_TRANSITION_MS = 350;
export const FEEDBACK_OFFSET_TOP = 8;
export const FEEDBACK_HEIGHT_ESTIMATE = 160;

export const INPUT_PART_STORAGE_KEY_PREFIX = 'ai-assistant-active-input-part';

export const PART_TYPES = {
  MESSAGE: 'MESSAGE',
  GUIDED_PROMPT: 'GUIDED_PROMPT',
  CHART: 'CHART',
};

export const QUESTION_TYPES = {
  SINGLE_SELECT: 'SINGLE_SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
};

// TODO: Remove SPOOFED_PROMPT_DATASETS and PROMPT_TRIGGERS once the backend streams parts natively.

export const PROMPT_TRIGGERS = {
  SINGLE_SELECT: 'test:prompt',
  MULTI_SELECT: 'test:prompt:multi',
  REQUIRED: 'test:prompt:required',
  STEPS: 'test:prompt:steps',
  BAR_CHART: 'test:bar-chart',
  BAR_CHART_SINGLE_SERIES: 'test:bar-chart:single-series',
  BAR_CHART_THREE_SERIES: 'test:bar-chart:three-series',
  BAR_CHART_SPARSE: 'test:bar-chart:sparse',
  BAR_CHART_DENSE: 'test:bar-chart:dense',
  BAR_CHART_INVALID: 'test:bar-chart:invalid',
};

export const SPOOFED_PROMPT_DATASETS = {
  [PROMPT_TRIGGERS.SINGLE_SELECT]: [
    {
      text: 'Which cohort would you like to compare against?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: 'Private' },
        { label: 'Public' },
        { label: 'Catholic' },
        { label: 'Regional' },
      ],
      required: false,
    },
  ],
  [PROMPT_TRIGGERS.MULTI_SELECT]: [
    {
      text: 'Which metrics are most important to you?',
      questionType: QUESTION_TYPES.MULTI_SELECT,
      options: [
        { label: 'Enrollment rate' },
        { label: 'Retention rate' },
        { label: 'Graduation rate' },
        { label: 'Yield rate' },
      ],
      required: true,
    },
  ],
  [PROMPT_TRIGGERS.REQUIRED]: [
    {
      text: 'Which academic year should the comparison cover?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: '2023-2024' },
        { label: '2024-2025' },
        { label: '2025-2026' },
      ],
      required: true,
    },
  ],
  [PROMPT_TRIGGERS.STEPS]: [
    {
      text: 'Which cohort would you like to compare against?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: 'Private' },
        { label: 'Public' },
        { label: 'Catholic' },
        { label: 'Regional' },
      ],
      required: true,
    },
    {
      text: 'Which metrics are most important to you?',
      questionType: QUESTION_TYPES.MULTI_SELECT,
      options: [
        { label: 'Enrollment rate' },
        { label: 'Retention rate' },
        { label: 'Graduation rate' },
        { label: 'Yield rate' },
      ],
      required: false,
    },
    {
      text: 'Which academic year should the comparison cover?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: '2023-2024' },
        { label: '2024-2025' },
        { label: '2025-2026' },
      ],
      required: false,
    },
  ],
};

export const SPOOFED_CHART_DATASETS = {
  [PROMPT_TRIGGERS.BAR_CHART]: {
    text: 'Enrollment growth trend by college',
    chart: {
      chartType: 'BAR',
      title: 'YoY enrollment growth by college',
      subtitle: 'Mocked MARK dataset',
      format: 'PERCENT',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Other',
        ],
      },
      series: [
        { label: 'Fall 2024', values: [3.2, 4.1, -1.5, 5.8, 2.3] },
        { label: 'Fall 2025', values: [4.5, 5.2, 0.8, 6.1, 3.0] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_SINGLE_SERIES]: {
    text: 'Single-series bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Applicant growth by quarter',
      subtitle: 'Single series',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Quarter',
        categories: ['Q1', 'Q2', 'Q3', 'Q4'],
      },
      series: [{ label: '2025', values: [2.1, 3.8, -0.6, 4.3] }],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_THREE_SERIES]: {
    text: 'Three-series bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Applications by college and year',
      subtitle: 'Three series',
      format: 'NUMBER',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Other',
        ],
      },
      series: [
        { label: '2023', values: [1200, 950, 780, 1100, 420] },
        { label: '2024', values: [1330, 1015, 820, 1185, 455] },
        { label: '2025', values: [1410, 1080, 860, 1260, 490] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_SPARSE]: {
    text: 'Sparse-data bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Regional conversion rates',
      subtitle: 'Includes missing data points',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Region',
        categories: [
          'Northeast',
          'Southeast',
          'Midwest',
          'West',
          'International',
        ],
      },
      series: [
        { label: '2024', values: [4.2, null, 3.6, null, 2.8] },
        { label: '2025', values: [4.9, 3.7, null, 4.1, null] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_DENSE]: {
    text: 'Dense-category bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Monthly inquiries by segment',
      subtitle: 'Larger category count for stress testing',
      format: 'NUMBER',
      categoryAxis: {
        label: 'Month',
        categories: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
      },
      series: [
        {
          label: '2025',
          values: [320, 345, 360, 380, 410, 430, 450, 470, 495, 520, 545, 570],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_INVALID]: {
    text: 'Invalid bar chart payload (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Invalid chart payload',
      subtitle: 'Series length does not match categories',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Quarter',
        categories: ['Q1', 'Q2', 'Q3'],
      },
      series: [{ label: '2025', values: [12, 20] }],
    },
  },
};
